/**
 * 托管账户管理
 * 目前接入两种托管账户：copper 和 fireblock
 * 场景：创建子账号和API管理页面
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useUserStore } from '@/store/useUser';
import {
  escrowInstitutionCodes,
  escrowInstitutionList,
  IEscrowInstitutionCodes,
} from '@/utils/pageConfig/openApi';
import { EscrowInstitutionNotExist } from '@/utils/trace-list/openApi';

import { useMemberInfo } from './useMemberInfo';

const useEscrowAccount = () => {
  const [search] = useSearchParams();
  const apiType = search.get('type') || 'system';
  const { userProfile } = useUserStore();

  const [institutionInfoCode, setInstitutionInfoCode] = useState<
    IEscrowInstitutionCodes | ''
  >('');
  const [accountTypeName, setAccountTypeName] = useState('');
  const [authName, setAuthName] = useState('');
  const [needApiAuth, setNeedApiAuth] = useState<boolean>(false);
  const [needSubApiAuth, setNeedSubApiAuth] = useState<boolean>(false);
  const [isFirstCreateApi, setIsFirstCreateApi] = useState<boolean>(false);

  const { loading, memberInfo } = useMemberInfo();

  const [onlySetUtaDepositInstitution, setOnlySetUtaDepositInstitution] =
    useState(false);

  const isSubAccount = useMemo(() => {
    const isMainAccountForSubAccount =
      search.has('subAccountId') || search.has('subaccount_id');
    return userProfile?.userInfo?.is_submember || isMainAccountForSubAccount;
  }, [userProfile, search]);

  const checkCooperUser = (source = '', isSubAccount = false) => {
    const sourceMap = source?.split(',') || [];
    const institutionInfo = escrowInstitutionList.find(({ code }) =>
      sourceMap.includes(code),
    );

    if (institutionInfo) {
      const { customAuthName, institutionName, code } = institutionInfo;

      // 设置托管商的展示给用户的名称
      setAccountTypeName(institutionName);

      // 设置授权弹窗授权时需要传给接口的托管商认证名称（有可能跟托管商code不同）
      setAuthName(customAuthName || code);

      // 设置当前托管商的code
      setInstitutionInfoCode(code);

      if (!isSubAccount) {
        // 需要显示api授权确认弹窗，仅母账号有返回
        setNeedApiAuth(!sourceMap.includes('auth-openapi'));
      }

      // 仅支持设置将资金充值至uta帐户的托管账户（目前仅 cooper 托管账户支持uta以外的账户）- 设置页面在用
      setOnlySetUtaDepositInstitution(institutionInfoCode !== 'clearloop');
    } else if (sourceMap.length) {
      // 当source有值，但是不在前端支持的范围内时，上报该错误
      EscrowInstitutionNotExist(source);
    }

    // 子账号创建是否需要先授权自动创建API
    setNeedSubApiAuth(
      institutionInfo?.code === 'clearloop' &&
        !sourceMap.includes('auth-subopeapi'),
    );

    // 非子账号需要判断是否是第一次创建API
    if (!isSubAccount) {
      const checkFromQuery =
        window?.location?.pathname?.includes('add-secret-key') &&
        escrowInstitutionCodes.includes(apiType as IEscrowInstitutionCodes);
      const checkFromApi = sourceMap.includes('first-openapi');
      // 是否已授权过，但是是第一次创建API
      setIsFirstCreateApi(checkFromQuery || checkFromApi);
    }
  };

  useEffect(() => {
    if (!loading && memberInfo) {
      checkCooperUser(memberInfo.source, isSubAccount);
    }
  }, [loading, memberInfo, isSubAccount]);

  return {
    accountTypeName,
    authName,
    institutionInfoCode,
    onlySetUtaDepositInstitution,
    needApiAuth,
    needSubApiAuth,
    isFirstCreateApi,
    updateNeedApiAuth: (status: boolean) => {
      setNeedApiAuth(status);
    },
    updateNeedSubApiAuth: (status: boolean) => {
      setNeedSubApiAuth(status);
    },
  };
};

export default useEscrowAccount;
